import React, { useEffect, useState } from 'react';
import { Stack, Snackbar, Alert, Switch } from '@mui/material';
import DocumentUploadCard from './components/DocumentUploadCard';
import { useDocumentUploads } from './hooks/useDocumentUploads';
import clientGraphql, { useQuery } from '../../../common/apollo-graphql';
import { GET_REFERRAL_DOCUMENTS } from '../gql/Query';
import { gql, useMutation } from '@apollo/client';
import { SAVE_REFERRAL_QUESTIONS_MUTATION } from '../gql/Mutation';
import GlobalLoader from '../../common/components/loader/GlobalLoader';
import StringUtils from '../../common/services/StringUtils';

export const orderDocumentsName = 'order_documents';

const UploadSection = ({
  key,
  doc,
  loading,
  isSameOrg,
  uploads,
  addFiles,
  removeFile,
  renameFile,
  showUpload,
  setShowUpload,
  referralUuid,
  refetch,
  defaultChecked,
  isOrder,
}) => {
  const SAVE_REFERRAL = gql`
    mutation SaveReferral($params: String!) {
      save_referral(params: $params)
    }
  `;

  const [saveReferral] = useMutation(SAVE_REFERRAL, {
    client: clientGraphql
  });

  const [saveAnswer] = useMutation(SAVE_REFERRAL_QUESTIONS_MUTATION, {
    client: clientGraphql
  });

  const hasQuestion = !!doc.question && doc.document.name !== 'misc';
  const isShown = showUpload[doc.document.id] ?? defaultChecked ?? !hasQuestion;

  const handleToggle = async () => {
    const newValue = !isShown;

    setShowUpload((prev) => ({
      ...prev,
      [doc.document.id]: newValue,
    }));

    if (hasQuestion) {
      try {
        if (doc.document.name === orderDocumentsName) {
          const payload = {
            uuid: referralUuid,
            is_order: newValue,
          };

          saveReferral({
            variables: {
              params: JSON.stringify(payload),
            },
          }).catch((err) => {
            console.error("saveReferral failed:", err);
          });
        }

        await saveAnswer({
          variables: {
            uuid: referralUuid,
            answers: [
              {
                question: doc.question.question,
                name: doc.document.name,
                answer: newValue ? "Yes" : "No"
              },
            ],
          },
        });

        refetch();
      } catch (err) {
        console.error('Failed to save answer:', err);
      }
    }
  };

  const uploadCard = (
    <DocumentUploadCard
      key={key}
      title={doc.document.name === 'misc' ? 'Additional Documents' : StringUtils.toTitleCase(doc.document.name)}
      description={
        doc.document.name === 'misc'
          ? 'Use this section to upload documents that could be useful for the receiving provider that are not listed as an option above.'
          : ''
      }
      required={isOrder || (doc.required && !isSameOrg)}
      files={uploads[doc.document.name] || []}
      onFilesAdded={(files) => addFiles(doc.document.id, files)}
      onFileDelete={(fileObj) => removeFile(doc.document.id, fileObj)}
      onFileRename={(fileObj, newName) => renameFile(fileObj, newName)}
    />
  );

  if (hasQuestion && !loading) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          padding: '12px',
          border: '1px dashed #ccc',
          borderRadius: '16px',
          marginBottom: 8,
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: isShown ? 12 : 0,
          }}
        >
          <span>{doc.question.question}</span>
          <Switch checked={isShown} onChange={handleToggle} />
        </div>
        {isShown && uploadCard}
      </div>
    );
  }

  return uploadCard;
};

export default function ReferralDocuments({ referralUuid, isSameOrg, onDisableSendReferral, inDialog }) {
  const [fileObjectsRequired, setFileObjectsRequired] = useState({});
  const [uploadedFileObjects, setUploadedFileObjects] = useState([]);
  const [showAlert, setShowAlert] = useState(false);
  const [showError, setShowError] = useState(false);
  const [showUpload, setShowUpload] = useState({});

  const { data: referralData, refetch, loading } = useQuery(GET_REFERRAL_DOCUMENTS, {
    variables: { uuid: referralUuid },
    skip: !referralUuid,
    client: clientGraphql,
    fetchPolicy: 'cache-and-network',
  });

  const referral = referralData?.referral;

  const { uploads, addFiles, removeFile, renameFile, isLoading } = useDocumentUploads({
    referralUuid,
    refetch,
    uploadedFileObjects,
    onSuccess: () => setShowAlert(true),
    onError: () => setShowError(true),
  });

  // Initialize required file objects, uploaded files, and default switches
  useEffect(() => {
    if (referral?.provider && uploadedFileObjects.length === 0) {
      const req = {};
      referral.provider.documents.forEach((d) => {
        if (d.required) {
          req[d.document.name] = true;
        }
      });
      if (referral.receiver?.is_order) {
        req[orderDocumentsName] = true;
      }
      setFileObjectsRequired(req);
    }

    if (referral?.documents) {
      setUploadedFileObjects(referral.documents || []);
    }

    // pre-fill switches from referral.answers
    if (referral?.answers) {
      const initialShowUpload = {};
      referral.answers.forEach((ans) => {
        const doc = referral.provider.documents.find(
          (d) => d.document.name === ans.name
        );
        if (doc) {
          initialShowUpload[doc.document.id] = ans.answer.toLowerCase() === 'yes';
        }
      });
      setShowUpload(initialShowUpload);
    }
  }, [referral, uploadedFileObjects.length]);

  // Disable send referral if required files not uploaded
  useEffect(() => {
    if (onDisableSendReferral) {
      let isDisabled = false;
      Object.keys(fileObjectsRequired).forEach((docName) => {
        const uploaded = uploads[docName] || [];
        if (fileObjectsRequired[docName] && uploaded.length === 0) {
          isDisabled = true;
        }
      });
      onDisableSendReferral(isDisabled);
    }
  }, [fileObjectsRequired, uploads, onDisableSendReferral]);

  let order_doc = {
    document: {
      name: orderDocumentsName,
      id: orderDocumentsName,
    },
    isOrder: referral?.receiver?.is_order,
    question: referral?.receiver?.is_order ? null : {
      question: "Is this an order?"
    }
  }

  return (
    <div className="space-y-6">
      {(loading || isLoading) && <GlobalLoader inDialog={inDialog} />}
      <Stack spacing={4}>
        {[
          ...([order_doc]),
          ...(referral?.provider?.documents || []),
        ].map((doc) => {
          const defaultChecked =
            doc.document.name === orderDocumentsName
              ? !!referral?.is_order
              : referral?.answers?.find(
                (ans) => ans.name === doc.document.name
              )?.answer?.toLowerCase() === 'yes';

          return (
            <UploadSection
              loading={loading}
              key={doc.document.id}
              doc={doc}
              isSameOrg={isSameOrg}
              uploads={uploads}
              addFiles={addFiles}
              removeFile={removeFile}
              renameFile={renameFile}
              showUpload={showUpload}
              setShowUpload={setShowUpload}
              referralUuid={referralUuid}
              refetch={refetch}
              defaultChecked={defaultChecked}
              isOrder={doc.isOrder}
            />
          );
        })}
      </Stack>

      <Snackbar
        open={showAlert}
        autoHideDuration={3000}
        onClose={() => setShowAlert(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        sx={{
          '&.MuiSnackbar-anchorOriginTopRight': {
            top: inDialog ? 75 : 100,
          },
        }}
      >
        <Alert onClose={() => setShowAlert(false)} severity="success" sx={{ width: '100%' }}>
          Successfully uploaded files
        </Alert>
      </Snackbar>

      <Snackbar
        open={showError}
        autoHideDuration={3000}
        onClose={() => setShowError(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        sx={{
          '&.MuiSnackbar-anchorOriginTopRight': {
            top: inDialog ? 75 : 100,
          },
        }}
      >
        <Alert onClose={() => setShowError(false)} severity="error" sx={{ width: '100%' }}>
          Failed to upload files
        </Alert>
      </Snackbar>
    </div>
  );
}
